import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import Login from './Login';
import Signup from './Signup';
import VerifyEmail from './VerifyEmail';
import ForgotPassword from './ForgotPassword';

interface AuthWrapperProps {
  children: React.ReactNode;
}

type AuthView = 'login' | 'signup' | 'verify' | 'forgot-password';

/**
 * `?view=signup` lets other screens deep-link straight to registration.
 * `verify` is deliberately not accepted — it needs a pending email from signup.
 */
function viewFromQuery(raw: string | null): AuthView {
  return raw === 'signup' || raw === 'forgot-password' ? raw : 'login';
}

export default function AuthWrapper({ children }: AuthWrapperProps) {
  const { isAuthenticated, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [currentView, setCurrentView] = useState<AuthView>(() =>
    viewFromQuery(searchParams.get('view'))
  );
  const [pendingEmail, setPendingEmail] = useState('');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    switch (currentView) {
      case 'signup':
        return (
          <Signup
            onSwitchToLogin={() => setCurrentView('login')}
            onVerificationRequired={(email) => {
              setPendingEmail(email);
              setCurrentView('verify');
            }}
          />
        );

      case 'verify':
        return (
          <VerifyEmail
            email={pendingEmail}
            onVerified={() => {
              setPendingEmail('');
              setCurrentView('login');
            }}
            onBack={() => setCurrentView('signup')}
          />
        );

      case 'forgot-password':
        return (
          <ForgotPassword
            onBack={() => setCurrentView('login')}
            onSuccess={() => setCurrentView('login')}
          />
        );

      case 'login':
      default:
        return (
          <Login
            onSwitchToSignup={() => setCurrentView('signup')}
            onForgotPassword={() => setCurrentView('forgot-password')}
          />
        );
    }
  }

  return <>{children}</>;
}
